/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-dataset
 * @responsibility Hold render-time entity projections and Classroom collection status for workspace screens.
 * @state-owner TanStack Query owns the entities and all fetching; this store is a re-seedable projection
 * @allowed-imports Zustand; shared view types; demo dataset constants; transient reset registry
 * @forbidden network; API client; localStorage; authorization decisions
 * @public-api workspace shell and modules
 * @side-effects in-memory dataset updates only
 * @tests workspace-behaviour.test.ts
 */

import { create } from 'zustand';
import { ACTIVITIES, ASSISTANTS, CLASSES, QUESTIONS, RUNS, SEAT_SEED } from '../lib/demo-data';
import type {
  ActivityRecord,
  AnswerMark,
  AssistantRecord,
  ClassRecord,
  GuardianRecord,
  GuardianRequest,
  RunRecord,
  SeatConfidence,
  SeatRecord,
  SeatStatus,
} from '../lib/model';
import { registerWorkspaceTransientReset } from '../lib/transient-reset';

export interface AnswerSheet {
  readonly marks: readonly AnswerMark[];
  readonly reviewed: boolean;
}

export interface RunReportState {
  readonly approved: Readonly<Record<string, boolean>>;
  readonly comments: Readonly<Record<string, string>>;
}

export interface PromptSettings {
  readonly text?: string;
  readonly tone?: 'warm' | 'neutral' | 'strict';
  readonly length?: 'short' | 'mid' | 'long';
}

export interface RosterEntry {
  readonly phone: string;
  readonly relation: string;
}

function seedSeats(classes: readonly ClassRecord[]): Record<number, readonly SeatRecord[]> {
  const seats: Record<number, readonly SeatRecord[]> = {};
  classes.forEach((item, classIndex) => {
    const seed = SEAT_SEED[classIndex];
    seats[classIndex] = item.students.map((name, index) => {
      const status = (seed ? seed[index] : index % 4 === 0 ? 'online' : 'ok') as SeatStatus;
      const stuckMinutes = 3 + (name.charCodeAt(0) % 3);
      const needsTeacher = status === 'need' || status === 'stuck';
      return {
        name,
        progress: 7 + (name.charCodeAt(0) % 10),
        status,
        ...(status === 'stuck' ? { stuckMinutes } : {}),
        /** Offline seats go stale fast; an active one was seen moments ago. */
        lastActiveMinutes:
          status === 'offline' ? 12 + (name.charCodeAt(0) % 9) : needsTeacher ? stuckMinutes : 1,
        aiAttempts: needsTeacher ? 2 + (name.charCodeAt(0) % 2) : name.charCodeAt(0) % 2,
        ...(needsTeacher && name.charCodeAt(0) % 2 === 0
          ? { repeatedConcept: QUESTIONS[name.charCodeAt(0) % QUESTIONS.length].concept }
          : {}),
        /** A hand-raise is certain; an inferred stall is a guess that gets weaker over time. */
        confidence: (status === 'need'
          ? 'high'
          : status === 'stuck'
            ? 'medium'
            : status === 'offline'
              ? 'low'
              : 'high') as SeatConfidence,
        ...(status === 'need'
          ? { reason: 'AI 連續兩次提示後仍保留同一個錯誤' }
          : status === 'stuck'
            ? { reason: '同一題停留超過門檻，沒有新的作答動作' }
            : {}),
      };
    });
  });
  return seats;
}

export function answerKey(runId: string, student: string): string {
  return `${runId}|${student}`;
}

function seedAnswers(
  classes: readonly ClassRecord[],
  runs: readonly RunRecord[],
): Record<string, AnswerSheet> {
  /** What a student who is not stuck on this question might get. */
  const passing: AnswerMark[] = ['ok', 'fix', 'low'];
  const sheets: Record<string, AnswerSheet> = {};
  runs.forEach((run) => {
    const classroom = classes[run.classIndex];
    if (!classroom) return;
    /** Each paper shifts the rotation, so a concept can genuinely improve or slip between them. */
    const paper = run.id.charCodeAt(1) % 3;
    classroom.students.forEach((student, seat) => {
      /**
       * One concept each student keeps missing, paper after paper. Without a persistent gap the
       * next-lesson panel has nothing to recommend — a one-off slip is not a reteaching case.
       */
      const weak = student.charCodeAt(0) % QUESTIONS.length;
      const drift = (student.charCodeAt(0) + paper) % passing.length;
      /**
       * The source rotation left every student with one unjudged answer, so nothing could ever be
       * commented on automatically. Every other seat instead comes back fully judged, which is what
       * the review queue looks like in practice.
       */
      const settled = seat % 2 === 0;
      sheets[answerKey(run.id, student)] = {
        marks: QUESTIONS.map((_, index) => {
          if (index === weak) return 'no';
          const mark = passing[(index + drift) % passing.length];
          return settled && mark === 'low' ? 'ok' : mark;
        }),
        reviewed: false,
      };
    });
  });
  return sheets;
}

function seedGuardians(classes: readonly ClassRecord[]): Record<string, readonly GuardianRecord[]> {
  const guardians: Record<string, readonly GuardianRecord[]> = {};
  classes.forEach((item) => {
    item.students.forEach((student, index) => {
      const bucket = index % 4;
      guardians[student] =
        bucket === 0
          ? []
          : bucket === 1
            ? [{ relation: '媽媽', channel: 'line', address: `LINE ${student}`, state: 'pending' }]
            : bucket === 2
              ? [{ relation: '媽媽', channel: 'line', address: `LINE ${student}`, state: 'linked' }]
              : [
                  {
                    relation: '爸爸',
                    channel: 'sms',
                    address: `09xx-xxx-${100 + index}`,
                    state: 'linked',
                  },
                ];
    });
  });
  return guardians;
}

function seedRoster(classes: readonly ClassRecord[]): Record<string, RosterEntry> {
  const roster: Record<string, RosterEntry> = {};
  classes.forEach((item) => {
    item.students.forEach((student, index) => {
      if (index % 3 === 2) return;
      roster[student] = {
        phone: `09${String(10000000 + index * 137).slice(0, 8)}`,
        relation: index % 2 ? '媽媽' : '爸爸',
      };
    });
  });
  return roster;
}

interface DatasetState {
  classroomDirectoryStatus: 'denied' | 'error' | 'loading' | 'ready';
  assistantDirectoryStatus: 'denied' | 'error' | 'loading' | 'ready';
  classes: readonly ClassRecord[];
  activities: readonly ActivityRecord[];
  runs: readonly RunRecord[];
  seats: Readonly<Record<number, readonly SeatRecord[]>>;
  answers: Readonly<Record<string, AnswerSheet>>;
  reports: Readonly<Record<string, RunReportState>>;
  guardians: Readonly<Record<string, readonly GuardianRecord[]>>;
  guardianRequests: readonly GuardianRequest[];
  roster: Readonly<Record<string, RosterEntry>>;
  prompts: Readonly<Record<'run' | 'rep', PromptSettings>>;
  assistants: readonly AssistantRecord[];
  nextActivityIndex: number;
  nextRunIndex: number;
  nextAssistantIndex: number;

  hydrate(next: {
    classes?: readonly ClassRecord[];
    activities?: readonly ActivityRecord[];
    runs?: readonly RunRecord[];
    assistants?: readonly AssistantRecord[];
  }): void;
  clearConnected(): void;
  setClassroomDirectoryStatus(status: 'denied' | 'error' | 'loading' | 'ready'): void;
  setAssistantDirectoryStatus(status: 'denied' | 'error' | 'loading' | 'ready'): void;
  prependActivities(activities: readonly ActivityRecord[]): void;
  updateActivity(id: string, patch: Partial<ActivityRecord>): void;
  removeActivity(id: string): void;
  allocateActivityId(): string;
  prependRun(run: RunRecord): void;
  updateRun(id: string, patch: Partial<RunRecord>): void;
  allocateRunId(): string;
  setSeatStatus(classIndex: number, student: string, status: SeatStatus): void;
  setAnswerMark(runId: string, student: string, index: number, mark: AnswerMark): void;
  setAnswerReviewed(runId: string, student: string, reviewed: boolean): void;
  setReportApproved(runId: string, student: string, approved: boolean): void;
  setReportComment(runId: string, student: string, comment: string): void;
  setGuardians(student: string, guardians: readonly GuardianRecord[]): void;
  addGuardianRequest(request: GuardianRequest): void;
  removeGuardianRequest(index: number): void;
  setPrompt(kind: 'run' | 'rep', settings: PromptSettings): void;
  resetPrompt(kind: 'run' | 'rep'): void;
  saveAssistant(assistant: AssistantRecord): void;
  removeAssistant(id: string): void;
  setDefaultAssistant(id: string): void;
  allocateAssistantId(): string;
  reset(): void;
}

function initialState() {
  return {
    classroomDirectoryStatus: 'ready' as const,
    assistantDirectoryStatus: 'ready' as const,
    classes: CLASSES,
    activities: ACTIVITIES,
    runs: RUNS,
    seats: seedSeats(CLASSES),
    answers: seedAnswers(CLASSES, RUNS),
    reports: {} as Readonly<Record<string, RunReportState>>,
    guardians: seedGuardians(CLASSES),
    guardianRequests: [] as readonly GuardianRequest[],
    roster: seedRoster(CLASSES),
    prompts: { run: {}, rep: {} } as Readonly<Record<'run' | 'rep', PromptSettings>>,
    assistants: ASSISTANTS,
    nextActivityIndex: 5,
    nextRunIndex: 10,
    nextAssistantIndex: ASSISTANTS.length + 1,
  };
}

function connectedEmptyState() {
  return {
    classroomDirectoryStatus: 'loading' as const,
    assistantDirectoryStatus: 'loading' as const,
    classes: [] as readonly ClassRecord[],
    activities: [] as readonly ActivityRecord[],
    runs: [] as readonly RunRecord[],
    seats: {} as Readonly<Record<number, readonly SeatRecord[]>>,
    answers: {} as Readonly<Record<string, AnswerSheet>>,
    reports: {} as Readonly<Record<string, RunReportState>>,
    guardians: {} as Readonly<Record<string, readonly GuardianRecord[]>>,
    guardianRequests: [] as readonly GuardianRequest[],
    roster: {} as Readonly<Record<string, RosterEntry>>,
    prompts: { run: {}, rep: {} } as Readonly<Record<'run' | 'rep', PromptSettings>>,
    assistants: [] as readonly AssistantRecord[],
    nextActivityIndex: 1,
    nextRunIndex: 1,
    nextAssistantIndex: 1,
  };
}

const EMPTY_REPORT: RunReportState = { approved: {}, comments: {} };

export const useDatasetStore = create<DatasetState>((set, get) => ({
  ...initialState(),

  clearConnected: () => set({ ...connectedEmptyState() }),

  setClassroomDirectoryStatus: (classroomDirectoryStatus) => set({ classroomDirectoryStatus }),

  setAssistantDirectoryStatus: (assistantDirectoryStatus) => set({ assistantDirectoryStatus }),

  hydrate: (next) =>
    set((state) => {
      const classes = next.classes ?? state.classes;
      const runs = next.runs ?? state.runs;
      return {
        classes,
        activities: next.activities ?? state.activities,
        runs,
        assistants: next.assistants ?? state.assistants,
        seats: next.classes ? seedSeats(classes) : state.seats,
        answers: next.classes || next.runs ? seedAnswers(classes, runs) : state.answers,
        guardians: next.classes ? seedGuardians(classes) : state.guardians,
        roster: next.classes ? seedRoster(classes) : state.roster,
      };
    }),

  prependActivities: (activities) =>
    set((state) => ({ activities: [...activities, ...state.activities] })),

  updateActivity: (id, patch) =>
    set((state) => ({
      activities: state.activities.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    })),

  removeActivity: (id) =>
    set((state) => ({ activities: state.activities.filter((item) => item.id !== id) })),

  allocateActivityId: () => {
    const next = get().nextActivityIndex;
    set({ nextActivityIndex: next + 1 });
    return `a${next}`;
  },

  prependRun: (run) => set((state) => ({ runs: [run, ...state.runs] })),

  updateRun: (id, patch) =>
    set((state) => ({
      runs: state.runs.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    })),

  allocateRunId: () => {
    const next = get().nextRunIndex;
    set({ nextRunIndex: next + 1 });
    return `r${next}`;
  },

  setSeatStatus: (classIndex, student, status) =>
    set((state) => ({
      seats: {
        ...state.seats,
        [classIndex]: (state.seats[classIndex] ?? []).map((seat) =>
          seat.name === student ? { ...seat, status } : seat,
        ),
      },
    })),

  setAnswerMark: (runId, student, index, mark) =>
    set((state) => {
      const key = answerKey(runId, student);
      const sheet = state.answers[key];
      if (!sheet) return {};
      return {
        answers: {
          ...state.answers,
          [key]: {
            ...sheet,
            marks: sheet.marks.map((value, position) => (position === index ? mark : value)),
          },
        },
      };
    }),

  setAnswerReviewed: (runId, student, reviewed) =>
    set((state) => {
      const key = answerKey(runId, student);
      const sheet = state.answers[key];
      if (!sheet) return {};
      return { answers: { ...state.answers, [key]: { ...sheet, reviewed } } };
    }),

  setReportApproved: (runId, student, approved) =>
    set((state) => {
      const report = state.reports[runId] ?? EMPTY_REPORT;
      return {
        reports: {
          ...state.reports,
          [runId]: { ...report, approved: { ...report.approved, [student]: approved } },
        },
      };
    }),

  setReportComment: (runId, student, comment) =>
    set((state) => {
      const report = state.reports[runId] ?? EMPTY_REPORT;
      return {
        reports: {
          ...state.reports,
          [runId]: { ...report, comments: { ...report.comments, [student]: comment } },
        },
      };
    }),

  setGuardians: (student, guardians) =>
    set((state) => ({ guardians: { ...state.guardians, [student]: guardians } })),

  addGuardianRequest: (request) =>
    set((state) => ({ guardianRequests: [...state.guardianRequests, request] })),

  removeGuardianRequest: (index) =>
    set((state) => ({
      guardianRequests: state.guardianRequests.filter((_, position) => position !== index),
    })),

  setPrompt: (kind, settings) =>
    set((state) => ({
      prompts: { ...state.prompts, [kind]: { ...state.prompts[kind], ...settings } },
    })),

  resetPrompt: (kind) => set((state) => ({ prompts: { ...state.prompts, [kind]: {} } })),

  /** Saving the one marked default demotes whichever assistant held the flag before. */
  saveAssistant: (assistant) =>
    set((state) => {
      const known = state.assistants.some((item) => item.id === assistant.id);
      const next = known
        ? state.assistants.map((item) => (item.id === assistant.id ? assistant : item))
        : [...state.assistants, assistant];
      return {
        assistants: assistant.isDefault
          ? next.map((item) => ({ ...item, isDefault: item.id === assistant.id }))
          : next,
      };
    }),

  /** The default assistant answers every unassigned run, so it is never removable. */
  removeAssistant: (id) =>
    set((state) => ({
      assistants: state.assistants.filter((item) => item.id !== id || item.isDefault),
    })),

  setDefaultAssistant: (id) =>
    set((state) => ({
      assistants: state.assistants.some((item) => item.id === id)
        ? state.assistants.map((item) => ({ ...item, isDefault: item.id === id }))
        : state.assistants,
    })),

  allocateAssistantId: () => {
    const next = get().nextAssistantIndex;
    set({ nextAssistantIndex: next + 1 });
    return `as${next}`;
  },

  reset: () => set({ ...initialState() }),
}));

export function selectRunReport(state: DatasetState, runId: string): RunReportState {
  return state.reports[runId] ?? EMPTY_REPORT;
}

registerWorkspaceTransientReset('workspace-dataset', () => useDatasetStore.getState().reset());
